import React, {useEffect, useMemo, useRef, useState} from "react";
import Button from "../../../../../components/button/button.jsx";
import {Modal} from "../../../../../components/modal/modal.jsx";
import {useQuery} from "@tanstack/react-query";
import InputForm from "../../../../../components/input/input-form.jsx";
import {FaEraser, FaSearch} from "react-icons/fa";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import aggridUtils from "../../../../../utils/aggrid-utils.js";
import {AgGridReact} from "ag-grid-react";
import vmsService from "../../../../../services/web/vms-service.js";
import Icon from "../../../video-cameras/components/cell-renderers/icon.jsx";

const initialFilter = {
    name: "",
    vms: ""
};

const ModalCameras = ({ open, setOpen, vms, cameras, onSelectCamera, selectionRule, selectedCameras, isLoading }) => {
    const gridRef = useRef(null);

    const [filter, setFilter] = useState(initialFilter);
    const [rowData, setRowData] = useState([]);

    const applyFilters = (baseCameras, filter, selectionRule, selectedCameras) => {
        let filtered = [...baseCameras];

        if (selectionRule) {
            if (selectionRule.connectionType === "RTSP") {
                filtered = filtered.filter(
                    cam => cam.connectionType === "RTSP"
                );
            }

            if (selectionRule.dvrName) {
                filtered = filtered.filter(
                    cam => cam.dvrName === selectionRule.dvrName
                );
            } else {
                filtered = filtered.filter(
                    cam => !cam.dvrName
                );
            }
        }

        if (selectedCameras?.length) {
            const selectedIds = selectedCameras.map(c => c.id);

            filtered = filtered.filter(
                cam => !selectedIds.includes(cam.id)
            );
        }

        if (filter.name) {
            filtered = filtered.filter(
                cam =>
                    cam.cameraName
                        ?.toLowerCase()
                        .includes(filter.name.toLowerCase())
            );
        }

        if (filter.vms) {
            filtered = filtered.filter(
                cam => String(cam.vmsId) === String(filter.vms)
            );
        }

        return filtered;
    };

    useEffect(() => {
        if (!cameras) return;

        const result = applyFilters(
            cameras,
            filter,
            selectionRule,
            selectedCameras
        );

        setRowData(result);
    }, [cameras, filter, selectionRule, selectedCameras]);

    const clearFilter = () => {
        const emptyFilter = { name: "", vms: "" };
        setFilter(emptyFilter);

        const result = applyFilters(
            cameras,
            emptyFilter,
            selectionRule,
            selectedCameras
        );

        setRowData(result);
    };

    const columnDefs = useMemo(
        () => [
            { headerName: "ID", field: "id", width: 75 },
            { headerName: "", field: "iconColor", width: 50, cellRenderer: Icon },
            { headerName: "VMS", field: "vmsName", width: 150 },
            { headerName: "Nome", field: "cameraName", sort: "asc", flex: 1, comparator: aggridUtils.nullLastComparator },
            { headerName: "Marcadores", field: "labels", width: 150, comparator: aggridUtils.nullLastComparator },
            { headerName: "Gravador", field: "dvrName", width: 150, comparator: aggridUtils.nullLastComparator }
        ],
        []
    );

    return (
        <Modal open={open} setOpen={setOpen} title="Câmeras" className={"w-2/3"}>
            <Modal.Body className="flex flex-col gap-3">
                <div className="grid grid-cols-12 gap-4">
                    <InputForm
                        label="Nome"
                        value={filter.name}
                        onChange={(e) =>
                            setFilter({ ...filter, name: e.target.value })
                        }
                        classNameWrapper="col-span-7"
                    />

                    <div className="flex gap-4 items-end col-span-5">
                        <SelectComponent
                            label="VMS"
                            options={(vms || []).map((e) => ({
                                value: e.id,
                                label: e.vmsCorporationName || e.vmsUrl
                            }))}
                            value={
                                filter.vms
                                    ? (vms || [])
                                        .map((e) => ({
                                            value: e.id,
                                            label: e.vmsCorporationName || e.vmsUrl
                                        }))
                                        .find((opt) => opt.value === filter.vms)
                                    : null
                            }
                            onChange={(option) =>
                                setFilter({
                                    ...filter,
                                    vms: option?.value || ""
                                })
                            }
                            classNameWrapper="flex-1"
                        />

                        <div className="flex gap-2">
                            <Button target="primary-outline" onClick={clearFilter}>
                                <FaEraser />
                            </Button>

                            <Button target="primary">
                                <FaSearch />
                            </Button>
                        </div>
                    </div>
                </div>

                <div className="h-[400px] w-full">
                    <AgGridReact
                        ref={gridRef}
                        rowData={rowData}
                        columnDefs={columnDefs}
                        rowSelection="single"
                        suppressCellFocus
                        animateRows={false}
                        gridOptions={{ localeText: aggridUtils.localization() }}
                        loading={isLoading}
                        onRowDoubleClicked={(event) => {
                            onSelectCamera(event.data);
                        }}
                        theme={aggridUtils.generalTheme()}
                    />
                </div>
            </Modal.Body>

            <Modal.Footer className="p-2">
                <Button
                    target="primary"
                    onClick={() => {
                        const selected = gridRef.current.api.getSelectedRows();
                        if (!selected.length) return;
                        onSelectCamera(selected[0]);
                    }}
                >
                    Confirmar
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ModalCameras;
